import { gql, useQuery } from '@apollo/client';
import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';

import LoadingIndicator from 'primitives/LoadingIndicator';

import CompanyEventSlideOver from './CompanyEventSlideOver';

const GET_SYNDICATE_COMPANY_EVENTS = gql(`
  query GetSyndicateCompanyEvents($syndicateId: ID!) {
    syndicate(id: $syndicateId) {
      id
      companyEvents {
        id
        label
        type
        eventDate
        isAuthorizationRequired
        authorizationStatus
        description
        company {
          id
          name
        }
      }
    }
  }
`);

const CompanyEvents: FC = () => {
  const { syndicateId, companyEventId } = useParams<{
    syndicateId: string;
    companyEventId: string;
  }>();

  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const { loading, error, data } = useQuery(GET_SYNDICATE_COMPANY_EVENTS, {
    variables: { syndicateId },
  });

  useEffect(() => {
    if (companyEventId) {
      setSelectedEventId(companyEventId);
    }
  }, [companyEventId, setSelectedEventId]);

  if (loading) return <LoadingIndicator />;
  if (error || !data) return <ErrorMessage error={error} />;

  const events = data.syndicate.companyEvents;

  return (
    <SidebarLayout>
      <SidebarLayoutNavigation
        title="Company Events"
        subTitle="Review and manage company events requiring your attention"
      />
      <SidebarLayoutContent>
        <DataTable
          data={events}
          columns={[
            {
              label: 'Company',
              fieldName: 'company.name',
            },
            {
              label: 'Event Type',
              fieldName: 'type',
              type: 'COMPANY_EVENT_TYPE',
            },
            {
              label: 'Label',
              fieldName: 'label',
            },
            {
              label: 'Date',
              fieldName: 'eventDate',
              type: 'DATE',
            },
            {
              label: 'Authorization Required',
              fieldName: 'isAuthorizationRequired',
              type: 'BOOLEAN',
            },
            {
              label: 'Authorization Status',
              fieldName: 'authorizationStatus',
              type: 'COMPANY_EVENT_STATUS',
            },
          ]}
          onClick={row => setSelectedEventId(row.id)}
          emptyListTitle="No company events yet"
          emptyListDescription="Company events will appear here once they are added."
        />
        <CompanyEventSlideOver
          open={!!selectedEventId}
          eventId={selectedEventId}
          onClose={() => setSelectedEventId('')}
        />
      </SidebarLayoutContent>
    </SidebarLayout>
  );
};

export default CompanyEvents;
